import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { IntuitionModal } from './IntuitionModal';
import { findUnitByCode, getCompletionPercentage, CUSTOM_STYLES, ConceptDetailView, ConceptUnitListView } from './ConceptUnitContent';
import { UI_CONFIGS } from './ConceptUIConfigs';
import { useLoading } from 'shared/contexts/Loading';
import masterApiCall from 'shared/services/MasterDataApi';

// Function to map the new raw data structure to the expected UI structure
const mapNewDataToUI = (rawData) => {
    if (!rawData) return null; // Guard clause

    const conceptUnits = (rawData.conceptUnits || []).map((cu, cuIndex) => {
        const difficultyCode = cu.skillLevel.startsWith('B') ? 'B' : cu.skillLevel.startsWith('I') ? 'I' : 'A';

        // Aggregate materials from both unit level and concept level
        const unitMaterials = (cu.conceptMaterials || []).map(m => ({
            type: m.type.toLowerCase(),
            name: m.name,
            link: m.link
        }));

        const globalMaterials = (cuIndex === 0 ? (rawData.conceptMaterials || []) : [])
            .map(m => ({
                type: m.type.toLowerCase(),
                name: m.name,
                link: m.link
            }));

        const studyMaterials = [...unitMaterials, ...globalMaterials];

        // *** CRITICAL CHANGE: Group all assessments under the parent fact ***
        const intuitionFacts = (cu.conceptFacts || []).map((fact) => {
            let assessments = (fact.conceptAssessments || []).map((assessment, assIndex) => {
                let expectedAnswer;
                let questionText;

                // --- Heuristic Check ---
                // 1. Check if answer is explicitly T/F.
                // if (assessment.answer.toLowerCase() === 'true' || assessment.answer.toLowerCase() === 'false') {
                expectedAnswer = assessment.answer.toLowerCase() === 'true';
                questionText = assessment.question;
                // }
                //  else {
                //     // 2. If answer is free text, synthesize a T/F question using the fact text.
                //     expectedAnswer = true;
                //     questionText = `Is the following statement true: "${fact.fact.substring(0, 80)}..." (Original Q: ${assessment.question})?`;
                // }

                return {
                    question: questionText,
                    expectedAnswer: expectedAnswer,
                    rationale: assessment.rationale,
                    misconception: assessment.misconception
                };
            });

            // --- IMAGE MATERIAL FILTERING (NEW) ---
            const imageMaterials = (fact.conceptMaterials || [])
                .filter(m => m.type.toUpperCase() === 'IMAGE')
                .map(m => ({
                    name: m.name,
                    link: m.link
                }));

            // FALLBACK: If a fact has no assessments
            // if (assessments.length === 0) {
            //     assessments.push({
            //         question: `Is the core concept true: "${fact.fact.substring(0, 80)}..."?`,
            //         expectedAnswer: true,
            //         rationale: fact.description || 'This statement is considered the core fact of this element.',
            //         misconception: 'No formal assessment was provided, but the core fact should be verified.'
            //     });
            // }

            return {
                id: `${fact.code}`, // Single ID for the entire fact set
                fact: fact.fact, // Main fact text
                description: fact.description,
                order: fact.order,
                difficulty: fact.skillLevel,
                classLevel: `Grade ${8 + cuIndex}`,
                assessments: assessments,
                imageMaterials: imageMaterials // Store images found at the fact level
            };
        }).filter(fact => fact !== null); // Remove facts with no relevant content

        return {
            code: `CU${cuIndex + 1}`,
            name: cu.name,
            description: cu.description,
            order: cu.order,
            difficulty: difficultyCode,
            classLevel: `Grade ${8 + cuIndex}-${10 + cuIndex}`,
            studyMaterials: studyMaterials,
            intuitionFacts: intuitionFacts
        };
    });

    return {
        concept: rawData.name,
        // Keep units even if they only contain facts from the fallback mechanism
        conceptUnits: conceptUnits.filter(cu => cu.intuitionFacts.length > 0)
    };
};


const ConceptDetailsContent = ({ conceptCode, navigateTo, displayToast }) => {
    // isDarkMode state removed as it is no longer required

    const { startLoading, stopLoading } = useLoading();

    // Stores the MAPPED data, not the raw data
    const [conceptData, setConceptData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // REMOVED: const [isDarkMode, setIsDarkMode] = useState(false);

    const [uiTheme, setUiTheme] = useState('INTUITION'); // 'TREE' or 'FUEL'
    const [currentCUCode, setCurrentCUCode] = useState(null); // null for list view
    const [selectedIF, setSelectedIF] = useState(null); // Fact object for modal
    const [completionStatus, setCompletionStatus] = useState({});

    const UI_CONFIG = useMemo(() => UI_CONFIGS[uiTheme], [uiTheme]);

    // Safe access to units, defaults to empty array if data isn't loaded yet
    const conceptUnits = conceptData ? conceptData.conceptUnits : [];

    // --- Fetch Data Effect ---
    useEffect(() => {
        const fetchConceptDetails = async () => {
            setError(null);
            setConceptData(null); // Clear old data

            try {
                // Trigger loading context
                // Note: masterApiCall usually handles start/stop loading, but we handle local loading state here too
                await masterApiCall(
                    "/conceptDetails?type=code&identifier=" + conceptCode,
                    null,
                    "GET",
                    null,
                    startLoading,
                    stopLoading,
                    null
                ).then((data) => {
                    // MAP DATA HERE, once it arrives
                    const mappedData = mapNewDataToUI(data);
                    setConceptData(mappedData);
                }).catch((error) => {
                    console.error("Error fetching study materials:", error);
                    setError(true);
                });
            } catch (err) {
                setError(err.message || 'Failed to fetch concepts.');
            } finally {
                setLoading(false);
            }
        };

        fetchConceptDetails();
    }, [conceptCode, startLoading, stopLoading]);

    // --- Initialize Completion Status Effect ---
    // This runs once valid conceptData is loaded to set up the mock completion state
    useEffect(() => {
        if (conceptData && conceptData.conceptUnits.length > 0) {
            const status = {};
            if (conceptData.conceptUnits[0].intuitionFacts.length > 0) {
                const cuCode = conceptData.conceptUnits[0].code;
                const factId = conceptData.conceptUnits[0].intuitionFacts[0].id;
                status[cuCode] = { [factId]: true };
            }
            setCompletionStatus(status);
        }
    }, [conceptData]);

    // REMOVED: Effect to apply dark class to the root element. Tailwind handles it now.
    /*
    useEffect(() => {
        const root = document.documentElement;
        if (isDarkMode) {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, [isDarkMode]);
    */

    // Effect to update document title on theme or view change
    useEffect(() => {
        // Guard clause: Don't try to set title if data isn't loaded
        if (!conceptData) return;

        const titlePart = currentCUCode
            ? findUnitByCode(currentCUCode, conceptUnits)?.name
            : `${UI_CONFIG.tree.name}`;

        document.title = `${titlePart}: ${conceptData.concept}`;
    }, [uiTheme, currentCUCode, UI_CONFIG, conceptUnits, conceptData]);


    // Handlers
    const handleCheckFact = useCallback((factId) => {
        // Update completion status in App state ONLY if perfect score was achieved in modal
        setCompletionStatus(prevStatus => ({
            ...prevStatus,
            [currentCUCode]: {
                ...(prevStatus[currentCUCode] || {}),
                [factId]: true,
            },
        }));
    }, [currentCUCode]);

    const handleOpenModal = useCallback((fact) => {
        setSelectedIF(fact);
    }, []);

    const handleCloseModal = useCallback(() => {
        setSelectedIF(null);
        // Ensure detail view updates after closing the modal (for completion status)
        if (currentCUCode) setCurrentCUCode(currentCUCode);
    }, [currentCUCode]);

    // Function to calculate percentage for the ConceptListView to use
    const getUnitCompletion = useCallback((unitCode) => {
        const cu = findUnitByCode(unitCode, conceptUnits);
        return getCompletionPercentage(cu, completionStatus);
    }, [conceptUnits, completionStatus]);

    // --- Render Guard ---
    if (loading || !conceptData) {
        // Removed explicit dark: classes from this block, relying on global styles where possible or defaulting to light/dark system settings.
        return <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="text-gray-500">Loading concept data...</div>
        </div>;
    }

    if (error) {
        // Removed explicit dark: classes from this block
        return <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="text-red-500">Error loading data. Please try again.</div>
        </div>;
    }

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: CUSTOM_STYLES }} />
            {/* The main container, taking over the role of the body */}
            <div className="min-h-screen transition-colors duration-500">
                {/* Header/Controls - Commented out the dark mode switch */}
                {/* <div className="fixed top-4 right-4 z-50 flex flex-col md:flex-row items-end md:items-center bg-white dark:bg-gray-800 p-3 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">

                    <div className="flex items-center mb-2 md:mb-0 md:mr-4">
                        <label htmlFor="dark-switcher" className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Dark Mode:</label>
                        <input
                            type="checkbox"
                            id="dark-switcher"
                            checked={isDarkMode}
                            onChange={() => setIsDarkMode(prev => !prev)}
                            className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                        />
                    </div>

                    <label htmlFor="ui-switcher" className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">UI Theme:</label>
                    <select id="ui-switcher" value={uiTheme} onChange={(e) => setUiTheme(e.target.value)}
                        className="block w-40 pl-3 pr-8 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md shadow-inner bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                        <option value="INTUITION">🌳 Intuition</option>
                        <option value="TREE">🌳 Knowledge Tree</option>
                        <option value="FUEL">⛽ Ready to Drive</option>
                    </select>
                </div> */}

                <div className="container mx-auto p-2 pt-2 md:p-2 md:pt-2"> {/* Increased top padding for fixed header */}
                    <div id="concept-title  p-2 sm:p-4" >
                        <span className="text-1xl md:text-2xl font-bold  p-2 sm:p-4">{UI_CONFIG.tree.icon} {UI_CONFIG.tree.name} :</span>
                        {/* dark: class already present */}
                        <span className="text-2xl md:text-4xl font-extrabold text-gray-800 dark:text-gray-100 mb-2"> {conceptData.concept}</span>
                    </div>

                    {currentCUCode ? (
                        <ConceptDetailView
                            cuCode={currentCUCode}
                            conceptUnits={conceptUnits}
                            UI_CONFIG={UI_CONFIG}
                            completionStatus={completionStatus}
                            getUnitCompletion={getUnitCompletion}
                            onBack={() => { setCurrentCUCode(null) }}
                            onOpenModal={handleOpenModal}
                        // isDarkMode prop removed
                        />
                    ) : (
                        <ConceptUnitListView
                            conceptUnits={conceptUnits}
                            UI_CONFIG={UI_CONFIG}
                            completionStatus={completionStatus}
                            getUnitCompletion={getUnitCompletion}
                            onSelectCU={setCurrentCUCode}
                            onBack={() => { setCurrentCUCode(null); navigateTo("concepts") }}
                        // isDarkMode prop removed
                        />
                    )}
                </div>

                {selectedIF && (
                    <IntuitionModal
                        selectedIF={selectedIF}
                        UI_CONFIG={UI_CONFIG}
                        onClose={handleCloseModal}
                        onCheckFact={handleCheckFact}
                        currentCUCode={currentCUCode}
                    // isDarkMode prop removed
                    />
                )}
            </div>
        </>
    );
};

export default ConceptDetailsContent;